import { useState, useCallback, useEffect } from 'react';

export interface DrawerSettings {
  hiddenFields: string[];
  /** Свёрнутые секции описания (slug'и body-полей). */
  collapsedBodyFields: string[];
  width: number;
  /** Порядок вкладок (ключи табов). Если не задан — порядок по умолчанию. */
  tabOrder?: string[];
  /** Высота верхней панели карточки (описание + свойства) в процентах, 20..80.
   *  undefined — авто-режим: высота по фактическому контенту (пустота не
   *  резервируется), пока пользователь сам не потянул сплиттер. */
  topSize?: number;
  /** Скелет карточки: 'classic' — двухпанельник + вкладки (по умолчанию);
   *  'stream' — лид-поле + свойства во всю ширину + вкладки, без title-шапки
   *  и без зарезервированной пустоты. Глобальная персональная настройка
   *  (общий ключ, не per-type), чтобы вид был единым во всех типах. */
  cardLayout: 'classic' | 'stream';
}

/** Легаси-дефолт верхней панели, %. Раньше hook записывал его в localStorage
 *  ВСЕМ пользователям при первом открытии карточки, поэтому «ровно 67» в
 *  сторадже неотличимо от «не трогал» — при загрузке считаем его нетронутым
 *  и мигрируем на авто-режим (topSize: undefined). Сознательный оверрайд
 *  пользователя почти никогда не попадает в ровно 67%. */
export const DEFAULT_TOP_SIZE = 67;

/** Ширина карточки в оконном (не-fullscreen) режиме, px. Бывший дефолт — 1100;
 *  «ровно 1100» в сторадже неотличимо от «не трогал» (оконный тогл писал именно
 *  1100), поэтому при загрузке считаем его нетронутым и подтягиваем актуальный
 *  дефолт. Сознательный ресайз почти никогда не даёт ровно 1100. */
export const COMPACT_WIDTH = 1200;
const LEGACY_COMPACT_WIDTH = 1100;

const STORAGE_KEY_PREFIX = 'lens:drawer:';

/** Глобальный ключ вида карточки — один на пользователя, вне per-type-настроек. */
const CARD_LAYOUT_KEY = 'lens:cardLayout';

function loadCardLayout(): 'classic' | 'stream' {
  try {
    return localStorage.getItem(CARD_LAYOUT_KEY) === 'stream' ? 'stream' : 'classic';
  } catch {
    return 'classic';
  }
}

function load(key: string): Partial<DrawerSettings> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + key);
    return raw ? (JSON.parse(raw) as Partial<DrawerSettings>) : {};
  } catch {
    return {};
  }
}

function save(key: string, settings: DrawerSettings) {
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + key, JSON.stringify(settings));
  } catch { /* quota exceeded */ }
}

export function useDrawerSettings(storageKey: string, fieldSlugs: string[]) {
  const [settings, setSettings] = useState<DrawerSettings>(() => {
    const saved = load(storageKey);
    const savedWidth = typeof saved.width === 'number' ? saved.width : COMPACT_WIDTH;
    return {
      hiddenFields: saved.hiddenFields ?? [],
      collapsedBodyFields: saved.collapsedBodyFields ?? [],
      width: savedWidth < 900 || savedWidth === LEGACY_COMPACT_WIDTH ? COMPACT_WIDTH : savedWidth,
      tabOrder: saved.tabOrder,
      topSize:
        typeof saved.topSize === 'number' && saved.topSize !== DEFAULT_TOP_SIZE
          ? Math.min(80, Math.max(20, saved.topSize))
          : undefined,
      cardLayout: loadCardLayout(),
    };
  });

  useEffect(() => {
    save(storageKey, settings);
  }, [storageKey, settings]);

  const toggleField = useCallback((slug: string) => {
    setSettings((prev) => {
      const hidden = prev.hiddenFields.includes(slug)
        ? prev.hiddenFields.filter((s) => s !== slug)
        : [...prev.hiddenFields, slug];
      return { ...prev, hiddenFields: hidden };
    });
  }, []);

  const resetSettings = useCallback(() => {
    setSettings((prev) => ({ hiddenFields: [], collapsedBodyFields: [], width: COMPACT_WIDTH, tabOrder: undefined, topSize: undefined, cardLayout: prev.cardLayout }));
  }, []);

  const setCardLayout = useCallback((cardLayout: 'classic' | 'stream') => {
    try { localStorage.setItem(CARD_LAYOUT_KEY, cardLayout); } catch { /* quota */ }
    setSettings((prev) => ({ ...prev, cardLayout }));
  }, []);

  const toggleBodyCollapsed = useCallback((slug: string) => {
    setSettings((prev) => {
      const collapsed = prev.collapsedBodyFields.includes(slug)
        ? prev.collapsedBodyFields.filter((s) => s !== slug)
        : [...prev.collapsedBodyFields, slug];
      return { ...prev, collapsedBodyFields: collapsed };
    });
  }, []);

  const setTopSize = useCallback((topSize: number) => {
    setSettings((prev) => ({ ...prev, topSize: Math.min(80, Math.max(20, Math.round(topSize))) }));
  }, []);

  const setWidth = useCallback((width: number) => {
    setSettings((prev) => ({ ...prev, width }));
  }, []);

  const setTabOrder = useCallback((tabOrder: string[]) => {
    setSettings((prev) => ({ ...prev, tabOrder }));
  }, []);

  const visibleSlugs = fieldSlugs.filter((s) => !settings.hiddenFields.includes(s));

  return {
    settings,
    visibleSlugs,
    toggleField,
    toggleBodyCollapsed,
    resetSettings,
    setWidth,
    setTabOrder,
    setTopSize,
    setCardLayout,
  };
}
